import type { IUserRepository } from "@src/domain/users/repository/user.repository.interface";
import type { UserDTO } from "../dto/user.dto";
import type { UpdateUserInputDTO } from "../dto/user-input.dto";
import { UserDtoMapper } from "../mappers/user-dto.mapper";

/**
 * Use Case: Update User
 * Updates user information (Admin only)
 */
export class UpdateUserUseCase {
    constructor(private readonly userRepository: IUserRepository) { }

    /**
     * Execute the use case
     * @param userId - User ID
     * @param data - Update data
     * @returns Promise<UserDTO> - Updated user DTO
     * @throws Error if validation fails
     */
    async execute(userId: string, data: UpdateUserInputDTO): Promise<UserDTO> {
        if (!userId || !userId.trim()) {
            throw new Error('User ID is required');
        }

        this.validateUpdateData(data);

        const user = await this.userRepository.update(userId, data);
        return UserDtoMapper.toDTO(user);
    }

    /**
     * Validate update data
     * @param data - The data to validate
     * @throws Error if validation fails
     */
    private validateUpdateData(data: UpdateUserInputDTO): void {
        if (data.email !== undefined && data.email !== null) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(data.email)) {
                throw new Error('Invalid email format');
            }
        }

        if (data.first_name !== undefined && data.first_name !== null && !data.first_name.trim()) {
            throw new Error('First name cannot be empty');
        }

        if (data.last_name !== undefined && data.last_name !== null && !data.last_name.trim()) {
            throw new Error('Last name cannot be empty');
        }
    }
}
